// ═══════════════════════════════════════════════════════════════
// PACKAGE FORM MODAL
// Path: src/components/shows/PackageFormModal.jsx
//
// Add/Edit a sponsorship package with BenefitCatalogPicker.
//
// Props:
//   pkg              — existing package object (null = create mode)
//   shows            — array of shows for the "Linked Show" dropdown
//   catalogTemplates — active benefit_templates rows for picker
//   onSave           — async (packageData) => void
//   onClose          — () => void
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 4
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Package } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY } from "../../constants/appConfig";
import { Modal } from "../ui/Modal";
import { FormInput, FormSelect } from "../ui/FormControls";
import { ActionButton } from "../ui/ActionButton";
import { BenefitCatalogPicker } from "./BenefitCatalogPicker";

export const PackageFormModal = ({
  pkg,
  shows = [],
  catalogTemplates = [],
  onSave,
  onClose,
}) => {
  const { palette } = useApp();
  const isEdit = !!pkg;

  const [name, setName] = useState(pkg?.name || "");
  const [minAmount, setMinAmount] = useState(
    pkg?.min_amount !== undefined ? String(pkg.min_amount) : ""
  );
  const [showId, setShowId] = useState(pkg?.show_id || "");
  const [benefits, setBenefits] = useState(pkg?.benefits || []);
  const [isActive, setIsActive] = useState(
    pkg?.is_active !== undefined ? pkg.is_active : true
  );
  const [notes, setNotes] = useState(pkg?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const showOptions = [
    { value: "", label: "Global (all shows)" },
    ...shows.map((show) => ({
      value: show.id,
      label: `${show.title} (${show.season})`,
    })),
  ];

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Package name is required.");
      return;
    }

    const amount = parseFloat(minAmount);
    if (isNaN(amount) || amount < 0) {
      setError("Minimum amount must be a valid number.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        minAmount: amount,
        showId: showId || null,
        benefits,
        isActive,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save package.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Package" : "Add Package"}
      icon={Package}
      onClose={onClose}
      width={720}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Row: Name + Min Amount */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 2 }}>
            <FormInput
              label="Package Name"
              value={name}
              onChange={(val) => { setName(val); setError(null); }}
              placeholder="e.g. Gold Show Sponsor"
              required
            />
          </div>
          <div style={{ flex: 1 }}>
            <FormInput
              label="Min Amount ($)"
              value={minAmount}
              onChange={(val) => { setMinAmount(val); setError(null); }}
              placeholder="e.g. 10000"
              type="number"
              required
            />
          </div>
        </div>

        {/* Linked show */}
        <FormSelect
          label="Linked Show"
          value={showId}
          onChange={setShowId}
          options={showOptions}
        />

        {/* Benefits picker */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
            }}
          >
            Benefits
          </label>
          <BenefitCatalogPicker
            benefits={benefits}
            onChange={setBenefits}
            catalogTemplates={catalogTemplates}
          />
        </div>

        {/* Notes */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
            }}
          >
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Internal notes"
            rows={2}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: FONT_SIZES.md,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
              resize: "vertical",
            }}
          />
        </div>

        {/* Active toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            id="pkg-active"
            style={{ width: 16, height: 16, cursor: "pointer" }}
          />
          <label
            htmlFor="pkg-active"
            style={{
              fontSize: FONT_SIZES.sm,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            Active (available for new sponsorships)
          </label>
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, paddingTop: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onClick={onClose} />
          <ActionButton
            label={saving ? "Saving..." : isEdit ? "Save Changes" : "Add Package"}
            onClick={handleSave}
            disabled={saving || !name.trim() || !minAmount}
          />
        </div>
      </div>
    </Modal>
  );
};
